import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NumberField, ReferenceField, TextField, useRecordContext } from "react-admin";
import { IfCanAccess } from "@react-admin/ra-rbac";
import DetailItem from "../../../components/Common/DetailItem";
import { StatusLabel } from "../../../components/Label/status";
import {
  ApiKeyValue,
  BooleanLabel,
  CoinhouseUserValue,
  ColorValue,
  CopyableValue,
  LogoPreview,
  ProjectSection,
  SavedPaymentPagePreview,
  monoSx,
  projectTypeLabel,
} from "./projectFields";

const logoSx = { width: 160, height: 64 };

const InfoDetails = () => {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  return (
    <ProjectSection id="info">
      <DetailItem label={t("Name")}>{project.name}</DetailItem>
      <DetailItem label={t("Account")}>
        <ReferenceField source="accountId" reference="accounts">
          <TextField source="name" sx={{ fontWeight: 600 }} />
        </ReferenceField>
      </DetailItem>
      <DetailItem label={t("Type")}>{t(projectTypeLabel(project.typeProject))}</DetailItem>
      <DetailItem label={t("Status")}>
        <StatusLabel fontSize="small" statusField="status" />
      </DetailItem>
      <DetailItem label={t("ID")} span={2}>
        <CopyableValue source="id" />
      </DetailItem>
      <DetailItem label={t("Code")} span={2}>
        <CopyableValue source="codeProject" />
      </DetailItem>
    </ProjectSection>
  );
};

const CryptoPaymentDetails = () => {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  return (
    <>
      <ProjectSection id="paymentPage">
        <SavedPaymentPagePreview />
        <DetailItem label={t("Email")}>
          <TextField source="parameters.physicalPayment.email" />
        </DetailItem>
        <DetailItem label={t("Phone")}>
          <TextField source="parameters.physicalPayment.phone" />
        </DetailItem>
        <DetailItem label={t("Address")}>
          <TextField source="parameters.physicalPayment.address" />
        </DetailItem>
        <DetailItem label={t("Inverted logo")}>
          <LogoPreview field="logo" sx={logoSx} />
        </DetailItem>
        <DetailItem label={t("Logo")}>
          <LogoPreview field="logoInverse" sx={logoSx} />
        </DetailItem>
        <DetailItem label={t("Colors")}>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <ColorValue source="backgroundColor" />
            <ColorValue source="fontColor" />
          </Box>
        </DetailItem>
      </ProjectSection>
      <ProjectSection id="cryptoPayment">
        <DetailItem label={t("Auto conversion")}>
          <BooleanLabel value={project.autoConvert} labels={["Enabled", "Disabled"]} />
        </DetailItem>
        <DetailItem label={t("Fees included")}>
          <BooleanLabel value={project.feesIncluded} />
        </DetailItem>
        <DetailItem label={t("API key")}>
          <ApiKeyValue />
        </DetailItem>
        <DetailItem label={t("Webhook URL")} span="all">
          <TextField source="parameters.webhookUrl" sx={monoSx} />
        </DetailItem>
      </ProjectSection>
    </>
  );
};

const GasStationDetails = () => {
  const { t }: { t: any } = useTranslation();
  const gasStation = useRecordContext()?.parameters?.gasStation ?? {};
  const whitelist = gasStation.destinationMode === "whitelist";
  return (
    <ProjectSection id="gasStation">
      <DetailItem label={t("Currency")}>
        <TextField source="parameters.gasStation.currency" />
      </DetailItem>
      <DetailItem label={t("Volume available per 24 hours")}>
        <TextField source="parameters.gasStation.limitPer24H" /> {gasStation.currency}
      </DetailItem>
      <DetailItem label={t("Approval threshold")}>
        <NumberField source="parameters.gasStation.approvalThresholdEur" options={{ style: "currency", currency: "EUR" }} />
      </DetailItem>
      <DetailItem label={t("Commission rate")}>
        <NumberField source="parameters.gasStation.commissionRate" options={{ style: "percent", maximumFractionDigits: 2 }} />
      </DetailItem>
      <DetailItem label={t("Destinations")}>{t(whitelist ? "Listed addresses only" : "Any address")}</DetailItem>
      {whitelist && (
        <DetailItem label={t("Allowed addresses")} span="all">
          <Box component="ul" sx={{ m: 0, pl: 2.5, ...monoSx }}>
            {(gasStation.allowedAddresses ?? []).map((address: string) => (
              <li key={address}>{address}</li>
            ))}
          </Box>
        </DetailItem>
      )}
      <DetailItem label={t("Webhook URL")} span="all">
        <TextField source="parameters.webhookUrl" sx={monoSx} />
      </DetailItem>
    </ProjectSection>
  );
};

const CoinhouseDetails = ({ gasStation }: { gasStation: boolean }) => {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  return (
    <IfCanAccess resource="projects.userIdCNHS" action="show">
      <ProjectSection id="coinhouse">
        <DetailItem label={t("Coinhouse user UUID")} span="all">
          <CoinhouseUserValue />
        </DetailItem>
        {gasStation && (
          <IfCanAccess resource="projects.fields.autorizedByCNHS" action="show">
            <DetailItem label={t("Authorized by Coinhouse")}>
              <BooleanLabel value={project.autorizedByCNHS} />
            </DetailItem>
          </IfCanAccess>
        )}
      </ProjectSection>
    </IfCanAccess>
  );
};

// The whole entity on one sheet: identity, then what its type configures, then the
// Coinhouse link.
const ProjectDetails = () => {
  const type = useRecordContext()?.typeProject;
  return (
    <>
      <InfoDetails />
      {type === "cryptoPayment" && <CryptoPaymentDetails />}
      {type === "gasStation" && <GasStationDetails />}
      <CoinhouseDetails gasStation={type === "gasStation"} />
    </>
  );
};

export default ProjectDetails;
